import React, { useState, useEffect } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, LayoutDashboard, LogOut } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';

const PUBLIC_NAV = [
  { to: '/', label: 'Home' },
  { to: '/about', label: 'About' },
  { to: '/subjects', label: 'Subjects' },
  { to: '/oxbridge', label: 'Oxbridge' },
  { to: '/contact', label: 'Contact' },
];

const Header: React.FC = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [open, setOpen] = useState(false);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const handleLogout = () => { logout(); navigate('/'); };

  return (
    <header className="fixed top-0 left-0 right-0 z-40 bg-paper/95 backdrop-blur border-b border-rule">
      <div className="container-content">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center gap-3 no-underline hover:no-underline">
            <img src="/ejl-logo.png" alt="" className="w-9 h-9 object-contain" />
            <span className="font-serif text-[19px] text-ink tracking-tight">EJL Education</span>
          </Link>

          {/* Desktop nav */}
          <nav className="hidden md:flex items-center gap-7">
            {PUBLIC_NAV.map(item => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) => `nav-link ${isActive ? 'active' : ''}`}
              >{item.label}</NavLink>
            ))}
            {/* Primary CTA. For anonymous visitors, the prospect-facing
                'Book a call' nudges them to /contact. For a logged-in
                student with consultation enabled on their profile, swap
                to a 'Consultation' shortcut into the in-app booking
                flow. Tutors / disabled-consultation students see no CTA. */}
            {!user ? (
              <Link to="/contact" className="btn btn-primary !py-2 !px-4 text-[14px]">Book a call</Link>
            ) : user.role === 'STUDENT' && user.studentProfile?.consultationEnabled ? (
              <Link to="/consultation" className="btn btn-primary !py-2 !px-4 text-[14px]">Consultation service</Link>
            ) : null}
            {user ? (
              <>
                <button onClick={() => navigate('/dashboard')} className="nav-link flex items-center gap-1.5" title="Dashboard">
                  <LayoutDashboard className="w-4 h-4" /> Dashboard
                </button>
                <button onClick={handleLogout} className="nav-link flex items-center gap-1.5" title="Log out">
                  <LogOut className="w-4 h-4" />
                </button>
              </>
            ) : (
              <Link to="/login" className="text-[13px] text-muted hover:text-ink no-underline">Login</Link>
            )}
          </nav>

          {/* Mobile toggle */}
          <button
            type="button"
            onClick={() => setOpen(v => !v)}
            className="md:hidden p-2 -mr-2 text-ink"
            aria-label={open ? 'Close menu' : 'Open menu'}
            aria-expanded={open}
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* Mobile drawer */}
      {open && (
        <div className="md:hidden border-t border-rule bg-paper">
          <nav className="container-content py-4 flex flex-col">
            {PUBLIC_NAV.map(item => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === '/'}
                className={({ isActive }) => `py-3 border-b border-rule nav-link ${isActive ? 'active' : ''}`}
              >{item.label}</NavLink>
            ))}
            {!user ? (
              <Link to="/contact" className="btn btn-primary mt-4 w-full">Book a £5 20-minute call</Link>
            ) : user.role === 'STUDENT' && user.studentProfile?.consultationEnabled ? (
              <Link to="/consultation" className="btn btn-primary mt-4 w-full">Consultation service</Link>
            ) : null}
            <div className="mt-4 flex items-center justify-between text-[13px] text-muted">
              {user ? (
                <>
                  <button onClick={() => navigate('/dashboard')} className="nav-link">Dashboard</button>
                  <button onClick={handleLogout} className="nav-link">Log out</button>
                </>
              ) : (
                <Link to="/login" className="nav-link">Login</Link>
              )}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;
